export type InitResult<TModel, TMsg> = { model: TModel; cmd: Cmd<TMsg> | null }
export type UpdateResult<TModel, TMsg> = InitResult<TModel, TMsg>
export type Cmd<T> = () => T | void | undefined | Promise<T | void | undefined>
export type Sub<T> = (el: HTMLElement, cb: (msg: T | null) => void) => () => void
export type Nothing = undefined | null
export type Maybe<T> = T | Nothing

type RecordUnion = Record<string | number | symbol, unknown>
type KeyOfUnion<U> = U extends RecordUnion ? keyof U : never
type ValueForKey<U, K extends string | number | symbol> = U extends Record<K, infer V> ? V : never

export function registerElement<TModel, TMsg>(
  name: string,
  options: Pick<MvuElement<TModel, TMsg>, 'init' | 'update' | 'view' | 'subscriptions'>
) {
  if (!customElements.get(name)) {
    customElements.define(
      name,
      class extends MvuElement<TModel, TMsg> {
        name = name
        init = options.init
        update = options.update
        view = options.view
        subscriptions = options.subscriptions
      }
    )
  }
}

abstract class MvuElement<TModel, TMsg> extends HTMLElement {
  protected name = 'MVU'
  private model: TModel
  private cleanup: () => void

  public connectedCallback() {
    const { model, cmd } = this.init(this)
    this.model = model
    this.debug('view', model)
    this.view(this.model)(this, msg => this.send(msg))
    if (cmd !== null) void this.exec(cmd)
    const subscribe = this.subscriptions(model)
    this.cleanup = subscribe(this, msg => this.send(msg))
  }

  public disconnectedCallback() {
    this.cleanup()
  }

  private debug(msg?: unknown, ...args: unknown[]) {
    // eslint-disable-next-line no-console
    console.debug(`${this.name}: ${String(msg)}`, ...args)
  }

  private send(msg: TMsg | null) {
    if (msg === null) return

    this.debug('update', msg)
    const { model, cmd } = this.update(msg, this.model)
    const isDirty = model !== this.model
    this.model = model

    if (isDirty) {
      this.debug('view', model)
      const render = this.view(model)
      render(this, msg => this.send(msg))
    }

    if (cmd !== null) void this.exec(cmd)
  }

  private async exec(cmd: Cmd<TMsg | null>) {
    const msg = await cmd()
    if (msg) this.send(msg)
  }

  abstract init(el: MvuElement<TModel, TMsg>): InitResult<TModel, TMsg>
  abstract update(msg: TMsg, model: TModel): UpdateResult<TModel, TMsg>
  abstract view(model: TModel): (el: MvuElement<TModel, TMsg>, sendMsg: (msg: TMsg) => void) => void
  abstract subscriptions(model: TModel): Sub<TMsg>
}

export function match<U extends RecordUnion, R>(
  record: U,
  handlers: { [K in KeyOfUnion<U>]: (value: ValueForKey<U, K>) => R }
): R {
  const key = Object.keys(record)[0] as KeyOfUnion<U>
  const value = record[key] as ValueForKey<U, typeof key>
  const resolve = handlers[key]
  return resolve(value)
}

export function mapMaybe<T, U>(maybe: Maybe<T>, cb: (value: T) => U): Maybe<U> {
  return isNothing(maybe) ? maybe : cb(maybe)
}

export function isNothing(maybe: Maybe<unknown>): maybe is Nothing {
  return maybe === null || maybe === undefined
}

export function mergeSubscriptions<T>(...fns: Sub<T>[]) {
  return (el: HTMLElement, cb: (msg: T) => void) => {
    const cleanupFns = fns.map(fn => fn(el, cb))
    return () => cleanupFns.forEach(fn => fn())
  }
}
